import { Brain, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { CRMNote } from '@/types/notes';
import { Pastille, SheetBlock } from '../sheet/EncreSheet';
import { BLOCK_ROW } from '../../bureau/encre';
import { useEncreTone } from '../../bureau/encreToneContext';

interface AccountBriefSectionProps {
  /** Briefs IA du compte (notes source='agent' taguées 'prebrief'), un par RDV. */
  briefs: CRMNote[];
  /** Ouvre le brief en modale (markdown rendu). */
  onView: (note: CRMNote) => void;
  /** `card` (the lead's sidebar) or `encre` (the account page, PLAN-ENCRE-OUTILS O4). */
  variant?: 'card' | 'encre';
}

/** Titre lisible : on retire l'emoji du titre stocké (« 🧠 Brief RDV — … ») ; fallback si absent. */
function briefLabel(note: CRMNote): string {
  const t = (note.title ?? '').replace(/^🧠\s*/, '').trim();
  return t || 'Brief de rendez-vous';
}

/**
 * Encart « Brief IA » : liste compacte des briefs pré-RDV générés par Otto
 * (loop-prebrief), un par rendez-vous. On affiche le TITRE + la date (pas le
 * markdown brut) ; le contenu complet s'ouvre en modale. Le brief reste une
 * note CRM — ce composant ne fait que le surfacer proprement.
 */
export function AccountBriefSection({ briefs, onView, variant: asked }: AccountBriefSectionProps) {
  // Without an explicit variant it follows the surface.
  const tone = useEncreTone();
  const variant = asked ?? (tone ? 'encre' : 'card');
  if (briefs.length === 0) return null;
  if (variant === 'encre') return <EncreBriefs briefs={briefs} onView={onView} />;

  return (
    <div className="bg-white rounded-xl border border-indigo-100 overflow-hidden">
      <div className="px-5 py-3 border-b border-indigo-100/70 bg-indigo-50/50 flex items-center gap-2">
        <Brain className="w-4 h-4 text-indigo-500" />
        <h2 className="font-semibold text-neutral-900 text-sm">Brief IA ({briefs.length})</h2>
      </div>
      <div className="divide-y divide-neutral-100">
        {briefs.map((brief) => (
          <button
            key={brief.id}
            type="button"
            onClick={() => onView(brief)}
            className="group w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-indigo-50/40 transition-colors"
            title="Ouvrir le brief"
          >
            <div className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-500 flex items-center justify-center shrink-0">
              <Brain className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-neutral-900 truncate group-hover:text-indigo-700">
                {briefLabel(brief)}
              </p>
              <p className="text-xs text-neutral-400">
                {format(new Date(brief.created_at), 'dd MMM yyyy', { locale: fr })}
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-neutral-300 shrink-0 group-hover:text-indigo-500" />
          </button>
        ))}
      </div>
    </div>
  );
}

/** The same briefs as a block of the Encre account page: one row per brief, opening it. */
function EncreBriefs({ briefs, onView }: Pick<AccountBriefSectionProps, 'briefs' | 'onView'>) {
  return (
    <SheetBlock title="Brief IA" count={briefs.length}>
      {briefs.map((brief) => (
        <button
          key={brief.id}
          type="button"
          onClick={() => onView(brief)}
          className={BLOCK_ROW}
          title="Ouvrir le brief"
        >
          <Pastille icon={Brain} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-desk-ink">{briefLabel(brief)}</span>
            <span className="block text-xs text-desk-soft">{format(new Date(brief.created_at), 'dd MMM yyyy', { locale: fr })}</span>
          </span>
          <ChevronRight size={16} className="shrink-0 text-desk-faint" aria-hidden="true" />
        </button>
      ))}
    </SheetBlock>
  );
}
